import { useState, useEffect } from "react";
import API from "@/lib/api";
import { formatCurrency, formatPercent } from "@/lib/formatters";
import DataTable from "@/components/shared/DataTable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Plus, Upload } from "lucide-react";
import AddEditDialog from "@/components/shared/AddEditDialog";
import UploadDialog from "@/components/shared/UploadDialog";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as ReTooltip, Legend,
} from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#151B24] border border-[#2D3748] rounded-sm p-3 text-sm shadow-xl">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-data text-xs" style={{ color: p.fill }}>{p.name}: {p.value}%</p>
      ))}
    </div>
  );
};

export default function SectorAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    API.get("/sectors")
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const sectors = data?.sectors || [];
  const benchmarks = data?.sector_benchmarks || [];

  const columns = [
    { key: "name", label: "Sector", sortable: false, render: (v) => <span className="font-medium text-foreground">{v}</span> },
    { key: "total_value", label: "Value", align: "right", render: (v) => formatCurrency(v) },
    { key: "allocation_pct", label: "Allocation", align: "right", render: (v) => `${v}%` },
    { key: "irr", label: "IRR", align: "right", render: (v, row) => (
      <span className={v > row.benchmark_irr ? "text-emerald-500" : "text-red-500"}>{v}%</span>
    )},
    { key: "benchmark_irr", label: "Benchmark", align: "right", render: (v) => `${v}%` },
    { key: "occupancy", label: "Occupancy", align: "right", render: (v) => `${v}%` },
    { key: "cap_rate", label: "Cap Rate", align: "right", render: (v) => `${v}%` },
    { key: "rental_growth", label: "Rent Growth", align: "right", render: (v) => <span className="text-emerald-500">+{v}%</span> },
    { key: "num_properties", label: "Properties", align: "right" },
  ];

  if (loading) return <div className="p-6"><Skeleton className="h-96" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="sector-analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Sector Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">Performance analysis by property sector</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-sector-btn" className="gap-1"><Plus size={14} />Add</Button>
          <Button size="sm" variant="secondary" onClick={() => setShowUpload(true)} data-testid="upload-sector-btn" className="gap-1"><Upload size={14} />Upload</Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <Card className="col-span-6 border border-border bg-card">
          <CardHeader className="border-b border-border p-3">
            <CardTitle className="text-sm font-semibold">IRR by Sector</CardTitle>
          </CardHeader>
          <CardContent className="p-4" data-testid="sector-irr-chart">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={sectors} margin={{ bottom: 30 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="#2D3748" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#9CA3AF", fontSize: 11 }} angle={-25} textAnchor="end" axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} domain={[0, 20]} />
                <ReTooltip content={<CustomTooltip />} />
                <Legend formatter={(v) => <span className="text-xs text-muted-foreground">{v}</span>} />
                <Bar dataKey="irr" name="Portfolio IRR" fill="#3B82F6" radius={[2, 2, 0, 0]} barSize={24} />
                <Bar dataKey="benchmark_irr" name="Benchmark IRR" fill="#6366F1" radius={[2, 2, 0, 0]} barSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="col-span-6 border border-border bg-card">
          <CardHeader className="border-b border-border p-3">
            <CardTitle className="text-sm font-semibold">Sector Value Allocation</CardTitle>
          </CardHeader>
          <CardContent className="p-4" data-testid="sector-value-chart">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={sectors} layout="vertical" margin={{ left: 100 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="#2D3748" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatCurrency(v)} />
                <YAxis type="category" dataKey="name" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} width={95} />
                <ReTooltip content={<CustomTooltip />} />
                <Bar dataKey="total_value" fill="#10B981" radius={[0, 2, 2, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-border bg-card">
        <CardHeader className="border-b border-border p-3">
          <CardTitle className="text-sm font-semibold">Sector Performance Detail</CardTitle>
        </CardHeader>
        <CardContent className="p-0" data-testid="sectors-table">
          <DataTable columns={columns} data={sectors} />
        </CardContent>
      </Card>
      <AddEditDialog open={showAdd} entityType="sectors" onClose={() => setShowAdd(false)} onSave={refresh} />
      <UploadDialog open={showUpload} entityType="sectors" onClose={() => setShowUpload(false)} onSuccess={refresh} />
    </div>
  );
}
